import * as React from 'react';
import { usePage } from '@inertiajs/react';
import { assetUrl } from '../../utils/assetUrl';
import { route } from '../../utils/route';
import { trackSignupClicked } from '../../utils/tracking';

interface GastroHeroProps {
  loginUrl: string;
  whatsappUrl: string;
  promoDiscount: number;
  title: string;
  subtitle: string;
  description: string;
}

export function GastroHero({ whatsappUrl, promoDiscount, title, subtitle, description }: GastroHeroProps) {
  const { cdnUrl } = usePage<{ cdnUrl: string }>().props;
  const heroImg = assetUrl(cdnUrl, '/img/gastro-hero.webp');

  return (
    <section className="gastro-hero">
      <style>{`
        .gastro-hero {
          position: relative;
          background: url(${heroImg}) center center/cover no-repeat;
          padding: 100px 0 110px;
          min-height: 480px;
          display: flex;
          align-items: center;
        }
        .gastro-hero::before {
          content: '';
          position: absolute;
          inset: 0;
          background: rgba(0, 0, 0, 0.65);
        }
        .gastro-hero-inner {
          position: relative;
          z-index: 1;
          max-width: 800px;
          margin: 0 auto;
          padding: 0 24px;
          text-align: center;
        }
        .gastro-hero h1 {
          font-size: 38px;
          font-weight: 800;
          color: #fff;
          margin: 0 0 16px;
          line-height: 1.2;
        }
        .gastro-hero .hero-sub {
          font-size: 20px;
          color: rgba(255,255,255,0.9);
          margin: 0 0 12px;
          font-weight: 500;
        }
        .gastro-hero .hero-desc {
          font-size: 17px;
          color: rgba(255,255,255,0.75);
          margin: 0 0 36px;
        }
        .gastro-hero-ctas {
          display: flex;
          gap: 16px;
          justify-content: center;
          flex-wrap: wrap;
        }
        .gastro-btn-primary {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 14px 28px;
          background: #1EAA25;
          color: #fff;
          font-size: 16px;
          font-weight: 600;
          border-radius: 8px;
          text-decoration: none;
          transition: background 0.2s;
        }
        .gastro-btn-primary:hover {
          background: #178f1e;
          color: #fff;
          text-decoration: none;
        }
        .gastro-btn-secondary {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 14px 28px;
          background: transparent;
          color: #fff;
          font-size: 16px;
          font-weight: 600;
          border: 2px solid rgba(255,255,255,0.6);
          border-radius: 8px;
          text-decoration: none;
          transition: all 0.2s;
        }
        .gastro-btn-secondary:hover {
          background: rgba(255,255,255,0.1);
          border-color: #fff;
          color: #fff;
          text-decoration: none;
        }
        .gastro-hero-promo {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          margin-top: 20px;
          padding: 10px 20px;
          background: rgba(30, 170, 37, 0.2);
          border: 1px solid rgba(30, 170, 37, 0.5);
          border-radius: 8px;
          color: #6ee674;
          font-size: 15px;
          font-weight: 500;
        }
        .gastro-hero-promo strong {
          color: #fff;
          font-weight: 700;
        }
        .gastro-hero-whatsapp {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          margin-top: 16px;
          color: rgba(255,255,255,0.6);
          font-size: 14px;
          text-decoration: none;
          transition: color 0.2s;
        }
        .gastro-hero-whatsapp:hover {
          color: #25d366;
          text-decoration: none;
        }
        @media (max-width: 640px) {
          .gastro-hero {
            padding: 70px 0 80px;
            min-height: auto;
          }
          .gastro-hero h1 {
            font-size: 28px;
          }
          .gastro-hero .hero-sub {
            font-size: 17px;
          }
          .gastro-hero-ctas {
            flex-direction: column;
            align-items: stretch;
          }
          .gastro-btn-primary, .gastro-btn-secondary {
            justify-content: center;
          }
          .gastro-hero-promo {
            display: block;
            text-align: center;
          }
        }
      `}</style>
      <div className="gastro-hero-inner">
        <h1>{title}</h1>
        <p className="hero-sub">{subtitle}</p>
        <p className="hero-desc">{description}</p>
        <div className="gastro-hero-ctas">
          <a href={route('wholesale.pricing')} className="gastro-btn-primary">
            <i className="fa fa-list-alt" /> Ver Precios Mayoristas
          </a>
          <a href={route('company.create')} className="gastro-btn-secondary" onClick={() => trackSignupClicked('gastronomicos', 'hero')}>
            <i className="fa fa-user-plus" /> Crear una cuenta mayorista
          </a>
        </div>
        <div className="gastro-hero-promo">
          <i className="fa fa-star" /> Tu primer mes con <strong>{promoDiscount}% de descuento</strong> en todos los productos
        </div>
        <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="gastro-hero-whatsapp">
          <i className="fa fa-whatsapp" /> ¿Dudas? Escribinos por WhatsApp
        </a>
      </div>
    </section>
  );
}
